/**
 * FR-E27: the IMS state of a GSTR-2B document — the chip on Reconciliation.
 *
 * IMS is the **portal-side** decision: what the taxpayer tells GSTN about a
 * supplier's document (accept, reject, keep pending, or nothing — which the
 * portal deems accepted). Suppress is **ProGST's own**: a row taken off the
 * worklist here, which the portal never hears of. The screen offers both and
 * says the difference once, in `IMS_VS_SUPPRESS`.
 *
 * **Neither moves the claim** (R-E2, owner 2026-10-16; gst spec D-137): the claim is the
 * portal's 2B, which reflects IMS once regenerated. Until the merge-in this file also counted
 * the documents a state took out of 4(A) for the GSTR-3B screen; that take-out is gone.
 */

import type { ImsStateOut } from './api'

/** Labels only; the state itself is served on every row. Held total against
 *  the service's `ims_states` by `seam-vocabularies.test.ts`, and equal to its
 *  `ims_state_labels`, so the chip and the Activity trail say the same. */
export const IMS_STATE_LABELS = {
  accepted: 'Accepted in IMS',
  rejected: 'Rejected in IMS',
  pending: 'Pending in IMS',
  no_action: 'No action in IMS (deemed accepted)',
} as const

export type ImsStateChoice = keyof typeof IMS_STATE_LABELS

/** The one sentence that tells the two decisions apart, where the buttons are. */
export const IMS_VS_SUPPRESS =
  'Accept, Reject and Keep pending are what the GST portal is told about a supplier’s document (IMS); Suppress leaves a row out of ProGST’s own reconciliation, and the portal never hears of it.'

/** A reason is required for these; the others may carry one. */
export const IMS_REASON_REQUIRED: ReadonlySet<string> = new Set(['rejected', 'pending'])

export function imsStateLabel(state: string): string {
  return (IMS_STATE_LABELS as Record<string, string>)[state] ?? 'An IMS state with no words yet'
}

/** The chip: the state in words, its reason, and whose word it is. */
export function imsChip(ims: ImsStateOut | null | undefined): string | null {
  if (!ims) return null
  const words = imsStateLabel(ims.state)
  const reason = ims.reason ? ` — ${ims.reason}` : ''
  const whose = ims.source === 'portal' ? ' (the portal’s own 2B)' : ''
  return `${words}${reason}${whose}`
}
